import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import authService from '../../api/authService';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  getPasswordStrengthErrors,
  validateLoggedInChangePasswordForm,
} from '../../utils/passwordValidation';

const EMPTY_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const ChangePasswordModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    if (errors.submit) {
      setErrors((prev) => ({ ...prev, submit: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateLoggedInChangePasswordForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await authService.changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );
      toast.success('Password changed successfully');
      handleClose();
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to change password. Please try again.'
      );
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordErrors = formData.newPassword
    ? getPasswordStrengthErrors(formData.newPassword)
    : [];
  const passwordStrength = formData.newPassword
    ? Math.max(0, 5 - passwordErrors.length)
    : 0;

  if (!open) {
    return null;
  }

  const modal = (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <div
        className="fixed inset-0 z-[200] bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />
      <DialogContent
        className="w-[95vw] sm:w-full max-w-md z-[210] max-h-[90vh] overflow-y-auto"
        onClose={handleClose}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#0B2530]">
            Change Password
          </DialogTitle>
          <p className="text-sm text-[#707070]">
            Enter your current password and choose a new secure password.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="loggedInCurrentPassword">Current Password</Label>
            <div className="relative">
              <Input
                id="loggedInCurrentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleChange('currentPassword', e.target.value)}
                className="pr-12"
                placeholder="Enter current password"
                autoComplete="current-password"
                style={{
                  borderColor: errors.currentPassword ? '#ef4444' : undefined,
                }}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#707070]"
                tabIndex={-1}
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="text-sm text-red-600">{errors.currentPassword}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="loggedInNewPassword">New Password</Label>
            <div className="relative">
              <Input
                id="loggedInNewPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={(e) => handleChange('newPassword', e.target.value)}
                className="pr-12"
                placeholder="Enter new password"
                autoComplete="new-password"
                style={{
                  borderColor: errors.newPassword ? '#ef4444' : undefined,
                }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#707070]"
                tabIndex={-1}
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {formData.newPassword && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className="h-1 flex-1 rounded"
                      style={{
                        background: level <= passwordStrength ? '#307A91' : '#E5E5E5',
                      }}
                    />
                  ))}
                </div>
                {passwordErrors.length > 0 ? (
                  <ul className="text-xs text-red-600 space-y-1">
                    {passwordErrors.map((msg) => (
                      <li key={msg} className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {msg}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Password meets all requirements
                  </p>
                )}
              </div>
            )}
            {errors.newPassword && (
              <p className="text-sm text-red-600">{errors.newPassword}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="loggedInConfirmPassword">Confirm New Password</Label>
            <div className="relative">
              <Input
                id="loggedInConfirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className="pr-12"
                placeholder="Confirm new password"
                autoComplete="new-password"
                style={{
                  borderColor: errors.confirmPassword ? '#ef4444' : undefined,
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#707070]"
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {errors.submit}
              </p>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="sm:min-w-[140px]">
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return createPortal(modal, document.body);
};

export default ChangePasswordModal;
